import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ROUTES } from "../config/routes";
import { PopupCampaignsAdminService } from "../api/generated/services/PopupCampaignsAdminService";
import { PopupCampaignAdminViewModel } from "../api/generated/models/PopupCampaignAdminViewModel";
import { PopupCampaignStatus } from "../api/generated/models/PopupCampaignStatus";
import { uploadImage, getImage } from "../api/images";
import { Image } from "../types";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { Label } from "../components/Label";
import { Toast } from "../components/Toast";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { ImageUpload } from "../components/ImageUpload";
import { StatusBadge } from "../components/PopupCampaigns/StatusBadge";
import { PopupCampaignStats } from "../components/PopupCampaigns/PopupCampaignStats";
import { AudienceCard } from "../components/PopupCampaigns/AudienceCard";
import { ReachTile } from "../components/PopupCampaigns/ReachTile";
import { PreviewModal } from "../components/PopupCampaigns/PreviewModal";
import { PopupCampaignAudienceEstimateViewModel } from "../api/generated/models/PopupCampaignAudienceEstimateViewModel";
import {
  PopupCampaignFormValues,
  validatePopupCampaignForm,
  toDatetimeLocal,
  datetimeLocalToIso,
  apiErrorMessage,
  isGuid,
  POPUP_BODY_MAX,
  POPUP_NAME_MAX,
  POPUP_CTA_TEXT_MAX,
  POPUP_CREATIVE_ASPECT,
  POPUP_MIN_CREATIVE_WIDTH,
  POPUP_MIN_CREATIVE_HEIGHT,
  buildPopupPreviewBranchLink,
} from "../helpers/popupCampaignForm";
import {
  TargetingFormState,
  TargetingErrors,
  EMPTY_TARGETING_FORM,
  toTargetingDto,
  fromTargetingViewModel,
  validateTargeting,
  hasLocationInput,
  targetingEquals,
} from "../helpers/popupCampaignTargeting";
import "../api/openApiConfig";

const NEW_ID = "new";

/** Tab identity lives in ?tab=, the convention every other tabbed admin page here uses. */
const TABS = [
  { key: "details", label: "Campaign details" },
  { key: "audience", label: "Audience" },
] as const;

type TabKey = (typeof TABS)[number]["key"];
const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6d4c91] focus:border-transparent";
const errorClass = "text-xs text-red-600 mt-1";

/** Bottom 15% of the crop box: content there may be hidden on small phones (spec §4.3). */
const SAFE_ZONE_STYLE: React.CSSProperties = {
  backgroundImage: "linear-gradient(to bottom, transparent 85%, rgba(220, 38, 38, 0.35) 85%)",
};

const toForm = (c: PopupCampaignAdminViewModel): PopupCampaignFormValues => ({
  name: c.name || "",
  imageAssetId: c.imageAssetId || "",
  body: c.body || "",
  ctaText: c.ctaText || "",
  ctaUrl: c.ctaUrl || "",
  startAt: toDatetimeLocal(c.startAt),
  endAt: toDatetimeLocal(c.endAt),
});

const AdminPopupCampaignEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === NEW_ID;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [campaign, setCampaign] = useState<PopupCampaignAdminViewModel | null>(null);
  const [image, setImage] = useState<Image | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [previewExpiresAt, setPreviewExpiresAt] = useState<unknown>(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [resetIdentityId, setResetIdentityId] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<
    "publish" | "unpublish" | "reset" | "previewToken" | "recalculate" | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState<"publish" | "unpublish" | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const [targeting, setTargeting] = useState<TargetingFormState>(EMPTY_TARGETING_FORM);
  const [savedTargeting, setSavedTargeting] = useState<TargetingFormState>(EMPTY_TARGETING_FORM);
  const [targetingErrors, setTargetingErrors] = useState<TargetingErrors>({});
  const [estimate, setEstimate] = useState<PopupCampaignAudienceEstimateViewModel | null>(null);
  const [resolvedCount, setResolvedCount] = useState<number | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveFailed, setResolveFailed] = useState(false);

  const patchTargeting = useCallback(
    (patch: Partial<TargetingFormState>) => setTargeting((previous) => ({ ...previous, ...patch })),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isDirty },
  } = useForm<PopupCampaignFormValues>({
    defaultValues: { name: "", imageAssetId: "", body: "", ctaText: "", ctaUrl: "", startAt: "", endAt: "" },
  });
  const bodyLength = (watch("body") || "").length;

  const loadReach = useCallback(async (campaignId: string) => {
    try {
      setEstimate(await PopupCampaignsAdminService.getApiPopupCampaignsAdminAudienceEstimate(campaignId));
    } catch (e) {
      console.error("Error loading audience estimate:", e);
      setToast({ message: apiErrorMessage(e, "Could not load the estimated reach"), type: "error" });
    }
  }, []);

  /** Issues a fresh preview token and returns its URL. Callers own the error reporting. */
  const issuePreviewToken = useCallback(async (campaignId: string) => {
    const issued = await PopupCampaignsAdminService.postApiPopupCampaignsAdminPreviewToken(campaignId);
    setPreviewUrl(issued.previewUrl || null);
    setPreviewToken(issued.token ?? null);
    setPreviewExpiresAt(issued.expiresAt ?? null);
    setPreviewError(null);
    return issued.previewUrl || null;
  }, []);

  /** Load-time / refresh-button path: never opens a tab, reports failure inside the panel. */
  const loadPreviewToken = useCallback(
    async (campaignId: string) => {
      setBusy("previewToken");
      try {
        await issuePreviewToken(campaignId);
      } catch (e) {
        console.error("Error creating preview link:", e);
        setPreviewError(apiErrorMessage(e, "Could not create a preview link"));
      } finally {
        setBusy(null);
      }
    },
    [issuePreviewToken]
  );

  const applyCampaign = useCallback(
    (c: PopupCampaignAdminViewModel) => {
      setCampaign(c);
      reset(toForm(c));
      const nextTargeting = fromTargetingViewModel(c.targeting);
      setTargeting(nextTargeting);
      setSavedTargeting(nextTargeting);
      if (c.imageAssetId && c.imageUrl) {
        setImage({ id: c.imageAssetId, url: c.imageUrl, title: c.name || "", contentType: "", width: 0, height: 0 });
      }
    },
    [reset]
  );

  const load = useCallback(async () => {
    if (!id || isNew) return;
    setLoading(true);
    try {
      const c = await PopupCampaignsAdminService.getApiPopupCampaignsAdmin1(id);
      applyCampaign(c);
      loadReach(id);
      loadPreviewToken(id);
    } catch (e) {
      console.error("Error loading pop-up campaign:", e);
      setToast({ message: apiErrorMessage(e, "Failed to load campaign"), type: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, isNew, applyCampaign, loadReach, loadPreviewToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Ask the server which postal codes the current location selection covers. Debounced because
  // the radius slider fires continuously while dragging. Depends on the location slice only —
  // not the whole `targeting` object — so typing in an unrelated field (age, balance, tenure) or
  // toggling a platform does not re-trigger the request.
  useEffect(() => {
    let current = true;

    if (!hasLocationInput(targeting)) {
      setResolvedCount(null);
      setResolveFailed(false);
      setResolving(false);
      return;
    }

    setResolving(true);
    setResolveFailed(false);
    const timer = setTimeout(async () => {
      try {
        const dto = toTargetingDto(targeting);
        const resolved = await PopupCampaignsAdminService.postApiPopupCampaignsAdminTargetingResolveLocation({
          location: dto?.location ?? {},
        });
        if (!current) return;
        setResolvedCount(resolved.count ?? 0);
      } catch (e) {
        console.error("Error resolving location:", e);
        if (!current) return;
        setResolvedCount(null);
        setResolveFailed(true);
      } finally {
        if (current) setResolving(false);
      }
    }, 400);

    return () => {
      current = false;
      clearTimeout(timer);
    };
    // Deliberately scoped to the location slice of `targeting` — see the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    targeting.radiusLat,
    targeting.radiusLon,
    targeting.radiusKm,
    targeting.locationPostalCodes,
    targeting.locationCities,
    targeting.locationRegions,
  ]);

  // The reach tile's copy promises the pending number "updates within a minute", but nothing
  // else refreshes it — poll while it is pending, stopping once it settles, on unmount, or after
  // a bounded number of attempts so a stuck backend cannot poll forever (8 * 15s = 2 minutes).
  useEffect(() => {
    if (isNew || !id || !estimate?.isPending) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      loadReach(id);
      if (attempts >= 8) clearInterval(interval);
    }, 15_000);

    return () => clearInterval(interval);
  }, [isNew, id, estimate?.isPending, loadReach]);

  // Preview tokens last 30 minutes; flag the panel the moment the current one lapses so the
  // admin sees a Refresh button instead of copying a link that will 401 on their phone.
  useEffect(() => {
    setPreviewStale(false);
    if (!previewExpiresAt) return;
    const remaining = new Date(previewExpiresAt as any).getTime() - Date.now();
    if (isNaN(remaining)) return;
    if (remaining <= 0) {
      setPreviewStale(true);
      return;
    }
    const timer = setTimeout(() => setPreviewStale(true), remaining);
    return () => clearTimeout(timer);
  }, [previewExpiresAt]);

  const handleImage = async (file: File | null) => {
    if (!file) return;
    try {
      const { id: assetId } = await uploadImage(file);
      const uploaded = await getImage(assetId);
      setImage(uploaded);
      setValue("imageAssetId", uploaded.id, { shouldDirty: true });
      clearErrors("imageAssetId");
    } catch (e) {
      console.error("Error uploading creative:", e);
      setToast({ message: apiErrorMessage(e, "Failed to upload creative"), type: "error" });
    }
  };

  const handleImageDelete = () => {
    setImage(null);
    setValue("imageAssetId", "", { shouldDirty: true });
  };

  const onSubmit = async (values: PopupCampaignFormValues) => {
    clearErrors();
    const trimmed = {
      ...values,
      name: values.name.trim(),
      body: values.body.trim(),
      ctaText: values.ctaText.trim(),
      ctaUrl: values.ctaUrl.trim(),
    };

    const validation = validatePopupCampaignForm(trimmed);
    (Object.keys(validation) as (keyof PopupCampaignFormValues)[]).forEach((field) =>
      setError(field, { type: "manual", message: validation[field] })
    );
    if (Object.keys(validation).length > 0) return;

    const targetingValidation = validateTargeting(targeting);
    setTargetingErrors(targetingValidation);
    if (Object.keys(targetingValidation).length > 0) return;

    // While the location resolution is still in flight (or its debounce hasn't fired yet),
    // resolvedCount is null — the same value it holds when nothing has ever run — so a save here
    // would go out with an unverified count. Make the admin wait for the check to finish instead.
    if (hasLocationInput(targeting) && resolving) {
      setToast({ message: "Still checking the selected area — try again in a moment", type: "error" });
      return;
    }

    // The backend rejects a location that covers no postal codes; catch it here so the admin gets
    // a message next to the control rather than a 400 after a save attempt.
    if (hasLocationInput(targeting) && resolvedCount === 0) {
      setToast({
        message: "The selected area covers no postal codes, so nobody could see this campaign",
        type: "error",
      });
      return;
    }

    const payload = {
      name: trimmed.name,
      imageAssetId: trimmed.imageAssetId,
      body: trimmed.body,
      ctaText: trimmed.ctaText,
      ctaUrl: trimmed.ctaUrl,
      startAt: datetimeLocalToIso(trimmed.startAt) as any,
      endAt: datetimeLocalToIso(trimmed.endAt) as any,
      targeting: toTargetingDto(targeting),
    };

    setSaving(true);
    try {
      if (isNew) {
        const created = await PopupCampaignsAdminService.postApiPopupCampaignsAdmin(payload);
        applyCampaign(created);
        setToast({ message: "Campaign created as draft", type: "success" });
        navigate(ROUTES.ADMIN_POPUP_CAMPAIGN_EDIT(created.id), { replace: true });
      } else {
        const updated = await PopupCampaignsAdminService.putApiPopupCampaignsAdmin(id!, { ...payload, id });
        applyCampaign(updated);
        loadReach(id!);
        setToast({ message: "Campaign saved", type: "success" });
      }
    } catch (e) {
      console.error("Error saving pop-up campaign:", e);
      setToast({ message: apiErrorMessage(e, "Failed to save campaign"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const runStatusChange = async (action: "publish" | "unpublish") => {
    if (!id) return;
    setBusy(action);
    try {
      const updated =
        action === "publish"
          ? await PopupCampaignsAdminService.postApiPopupCampaignsAdminPublish(id)
          : await PopupCampaignsAdminService.postApiPopupCampaignsAdminUnpublish(id);
      applyCampaign(updated);
      setToast({ message: action === "publish" ? "Campaign published" : "Campaign unpublished", type: "success" });
    } catch (e) {
      console.error(`Error on ${action}:`, e);
      setToast({
        message: apiErrorMessage(
          e,
          action === "publish" ? "Could not publish (is the end date in the past?)" : "Could not unpublish"
        ),
        type: "error",
      });
    } finally {
      setBusy(null);
      setShowPublishConfirm(null);
    }
  };

  const handleReset = async () => {
    const identityId = resetIdentityId.trim();
    if (!id || !isGuid(identityId)) return;
    setBusy("reset");
    try {
      await PopupCampaignsAdminService.deleteApiPopupCampaignsAdminInteractions(id, identityId);
      setResetIdentityId("");
      setToast({ message: "Interaction cleared — the campaign will show to that user again", type: "success" });
      const stats = await PopupCampaignsAdminService.getApiPopupCampaignsAdminStats(id);
      setCampaign((prev) => (prev ? { ...prev, stats } : prev));
      loadReach(id);
    } catch (e) {
      console.error("Error clearing interaction:", e);
      setToast({ message: apiErrorMessage(e, "Could not clear the interaction (check the identity id)"), type: "error" });
    } finally {
      setBusy(null);
      setShowResetConfirm(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await PopupCampaignsAdminService.deleteApiPopupCampaignsAdmin(id);
      navigate(ROUTES.ADMIN_POPUP_CAMPAIGNS());
    } catch (e) {
      console.error("Error deleting pop-up campaign:", e);
      setToast({ message: apiErrorMessage(e, "Failed to delete campaign"), type: "error" });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }
  if (!isNew && !campaign) {
    return <div className="text-center py-12 text-gray-500">Campaign not found.</div>;
  }

  const isActive = campaign?.status === PopupCampaignStatus.ACTIVE;
  const targetingDirty = !targetingEquals(targeting, savedTargeting);
  const canPublish = !isNew && !!campaign && !isActive && !!campaign.imageAssetId && !isDirty && !targetingDirty;
  const branchLink = previewUrl && previewToken ? buildPopupPreviewBranchLink(previewUrl, previewToken) : null;
  const dirty = isDirty || targetingDirty;
  // A new campaign has no audience or reach to show, so it stays on details whatever ?tab= says.
  const requestedTab = searchParams.get("tab");
  const activeTab: TabKey =
    isNew ? "details" : TABS.find((t) => t.key === requestedTab)?.key ?? "details";

  return (
    <div>
      <div className="mb-8">
        <button
          onClick={() => navigate(ROUTES.ADMIN_POPUP_CAMPAIGNS())}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Pop-up Campaigns
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-medium">{isNew ? "New Pop-up Campaign" : campaign?.name || "Edit Campaign"}</h2>
            {!isNew && <StatusBadge status={campaign?.status} />}
          </div>
          {!isNew && (
            <div className="flex items-center gap-2">
              {isActive ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPublishConfirm("unpublish")}
                  isLoading={busy === "unpublish"}
                  disabled={isDirty || targetingDirty}
                  title={isDirty || targetingDirty ? "Save your changes first" : undefined}
                >
                  Unpublish
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setShowPublishConfirm("publish")}
                  isLoading={busy === "publish"}
                  disabled={!canPublish}
                  title={
                    isDirty || targetingDirty
                      ? "Save your changes first"
                      : !campaign?.imageAssetId
                      ? "Add a creative first"
                      : undefined
                  }
                >
                  Publish
                </Button>
              )}
              <Button variant="outline" size="sm" type="button" onClick={() => setShowPreview(true)}>
                Campaign preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
        {!isNew && dirty && (
          <p className="text-xs text-gray-500 mt-2">Unsaved changes. Publishing is disabled until you save.</p>
        )}
      </div>

      {!isNew && (
        <div className="flex flex-wrap gap-1 mb-8 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSearchParams({ tab: tab.key })}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab.key === activeTab
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Both tabs live inside one form so a single submit saves whichever the admin edited.
          The inactive tab unmounts rather than hiding: Leaflet measures itself on mount and
          comes up zero-sized inside a display:none parent. React Hook Form keeps the values of
          unmounted fields (shouldUnregister defaults to false in v7), so the details survive a
          trip to the Audience tab. */}
      <form onSubmit={handleSubmit(onSubmit)} className="mb-8" noValidate>
        {activeTab === "details" && (
          <div className="space-y-6">
            {!isNew && <PopupCampaignStats stats={campaign?.stats} />}

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-medium text-gray-900 mb-4">Campaign details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign name (internal)</label>
                  <input type="text" maxLength={POPUP_NAME_MAX} {...register("name")} className={inputClass} />
                  {errors.name && <p className={errorClass}>{errors.name.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <ImageUpload
                    label={<Label additionalText="(4:5 portrait, min. 1080 × 1350 px, JPG/PNG, max 5 MB)">Creative</Label>}
                    images={image ? [image] : undefined}
                    onChange={handleImage}
                    onDelete={handleImageDelete}
                    aspectRatio={POPUP_CREATIVE_ASPECT}
                    cropAreaStyle={SAFE_ZONE_STYLE}
                    cropHint="The tinted bottom 15% may be hidden on small phones — keep logos and headlines above it. Do not include body text, the CTA button or the wave; the app draws those."
                    minCropSize={{ width: POPUP_MIN_CREATIVE_WIDTH, height: POPUP_MIN_CREATIVE_HEIGHT }}
                  />
                  <input type="hidden" {...register("imageAssetId")} />
                  {errors.imageAssetId && <p className={errorClass}>{errors.imageAssetId.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body text</label>
                  <textarea rows={3} maxLength={POPUP_BODY_MAX} {...register("body")} className={inputClass} />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-400">Around three lines on a phone.</span>
                    <span className="text-xs text-gray-400">{bodyLength}/{POPUP_BODY_MAX}</span>
                  </div>
                  {errors.body && <p className={errorClass}>{errors.body.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA button text</label>
                  <input type="text" maxLength={POPUP_CTA_TEXT_MAX} {...register("ctaText")} className={inputClass} />
                  {errors.ctaText && <p className={errorClass}>{errors.ctaText.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA destination</label>
                  <input type="text" placeholder="https://partner.dk/... or /?b=<businessId>" {...register("ctaUrl")} className={inputClass} />
                  <p className="text-xs text-gray-400 mt-1">Absolute http(s) URL opens in a new tab; a path starting with / navigates inside the app.</p>
                  {errors.ctaUrl && <p className={errorClass}>{errors.ctaUrl.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start (optional)</label>
                  <input type="datetime-local" {...register("startAt")} className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End (optional)</label>
                  <input type="datetime-local" {...register("endAt")} className={inputClass} />
                  {errors.endAt && <p className={errorClass}>{errors.endAt.message}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "audience" && !isNew && (
          <div className="space-y-6">
            <ReachTile
              estimate={estimate}
              loading={loading}
              recalculating={busy === "recalculate"}
              recalculateDisabled={targetingDirty}
              /* The estimate describes the SERVER's stored targeting, so the tile must too —
                 unsaved local edits are already surfaced by recalculateDisabled. */
              selectedPlatforms={savedTargeting.platforms}
              onRecalculate={async () => {
                if (!id) return;
                setBusy("recalculate");
                try {
                  setEstimate(await PopupCampaignsAdminService.postApiPopupCampaignsAdminEstimateRecalculate(id));
                  setToast({ message: "Recalculating — the number updates within a minute", type: "success" });
                } catch (e) {
                  console.error("Error requesting a recalculation:", e);
                  setToast({ message: apiErrorMessage(e, "Could not request a recalculation"), type: "error" });
                } finally {
                  setBusy(null);
                }
              }}
            />

            <AudienceCard
              form={targeting}
              onChange={patchTargeting}
              errors={targetingErrors}
              resolvedCount={resolvedCount}
              resolving={resolving}
              resolveFailed={resolveFailed}
            />
          </div>
        )}

        {/* One save button for both tabs. Sticky rather than scroll-measured: it trails the
            content and pins itself to the bottom of the viewport only while the page scrolls. */}
        <div className="sticky bottom-0 z-10 mt-6 flex items-center justify-end gap-3 rounded-lg border border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
          {!isNew && dirty && (
            <span className="mr-auto text-xs text-gray-500">You have unsaved changes.</span>
          )}
          <Button type="submit" isLoading={saving} disabled={!isNew && !dirty}>
            {isNew ? "Create draft" : "Save changes"}
          </Button>
        </div>
      </form>

      {!isNew && activeTab === "details" && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-medium text-gray-900 mb-1">Reset for a user (testing)</h3>
          <p className="text-xs text-gray-500 mb-3">
            Clears that user's seen/dismissed/clicked record so this campaign shows to them again. Also removes them from the stats above. Find the identity id in Users → Details.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={resetIdentityId}
              onChange={(e) => setResetIdentityId(e.target.value)}
              placeholder="Identity id (GUID)"
              className={`${inputClass} font-mono text-xs`}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              isLoading={busy === "reset"}
              disabled={!isGuid(resetIdentityId)}
            >
              Reset
            </Button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showPublishConfirm !== null}
        onClose={() => setShowPublishConfirm(null)}
        onConfirm={() => showPublishConfirm && runStatusChange(showPublishConfirm)}
        title={showPublishConfirm === "publish" ? "Publish campaign" : "Unpublish campaign"}
        message={
          showPublishConfirm === "publish"
            ? "Users will start seeing this pop-up as soon as it is inside its date window. Each user sees it once. Continue?"
            : "The pop-up stops showing immediately. Users who already saw it will not see it again if you republish. Continue?"
        }
        confirmText={showPublishConfirm === "publish" ? "Publish" : "Unpublish"}
        isLoading={busy === "publish" || busy === "unpublish"}
      />

      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset campaign for user"
        message={`Clear the interaction for identity ${resetIdentityId.trim()}? The campaign will show to this user again and their impression/dismissal/click is removed from the stats.`}
        confirmText="Reset"
        variant="danger"
        isLoading={busy === "reset"}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete campaign"
        message={`Delete "${campaign?.name || "this campaign"}"? Stats are kept internally but the campaign disappears from this list.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        previewUrl={previewUrl}
        branchLink={branchLink}
        expiresAt={previewExpiresAt}
        stale={previewStale}
        error={previewError}
        refreshing={busy === "previewToken"}
        onRefresh={() => id && loadPreviewToken(id)}
        onCopied={(message) => setToast({ message, type: "success" })}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminPopupCampaignEdit;
